import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { agriculturalAPI, chartsAPI } from '../services/api';
import {
  Globe,
  BarChart3,
  ArrowRight,
  DollarSign,
  Activity,
  TrendingUp,
  Search,
  Zap,
  CheckCircle2,
  Users,
  Target,
  Shield,
  Play,
  FileText,
  Award,
  Lightbulb,
  Settings,
  Database,
  Brain,
  Sparkles,
  Star,
  Quote,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  ChevronRight,
  Layers,
  PieChart,
  LineChart,
  Rocket,
  Infinity,
  Briefcase,
  Building2,
  Newspaper,
  Calendar as CalendarIcon,
  MessageSquare,
  Video,
  FolderKanban,
  GanttChart,
  MessageCircle,
  Users2,
  Network,
  TrendingDown,
  Filter,
  Download,
  Upload,
  Share2,
  Bell,
  BellRing,
  FileCheck,
  Presentation,
  Code,
  Server,
  Cloud,
  Lock,
  Eye,
  GitBranch,
  Timer,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  HelpCircle,
  BookOpen,
  GraduationCap,
  Mic,
  Camera,
  Share,
  Heart,
  ThumbsUp,
  MessageSquareMore,
  Hash,
  Tag,
  Image as ImageIcon,
  Link as LinkIcon,
  ExternalLink,
  BarChart,
  BarChart2,
  Kanban,
  FlaskConical,
  UserCircle,
} from 'lucide-react';

const EleavingLabHome: React.FC = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState('Explore');
  const [activeUserType, setActiveUserType] = useState(0);
  const [activeBackofficeTab, setActiveBackofficeTab] = useState('projects');
  const [activeRole, setActiveRole] = useState('researchers');
  const [stats, setStats] = useState({
    totalDocuments: 0,
    totalIndices: 0,
    totalCharts: 0,
    totalProjects: 0,
    totalUsers: 0,
    totalBusinesses: 0,
  });
  const [animatedStats, setAnimatedStats] = useState({
    totalDocuments: 0,
    totalIndices: 0,
    totalCharts: 0,
    totalProjects: 0,
    totalUsers: 0,
    totalBusinesses: 0,
  });
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    setMounted(true);
    loadStats();
    setupScrollAnimations();
    setupParticleAnimation();
  }, []);

  useEffect(() => {
    animateStats();
  }, [stats]);

  const setupScrollAnimations = () => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  };

  const setupParticleAnimation = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
    }> = [];

    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(6, 182, 212, ${particle.opacity})`;
        ctx.fill();
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  };

  const animateStats = () => {
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    Object.keys(stats).forEach((key) => {
      const target = stats[key as keyof typeof stats];
      const start = animatedStats[key as keyof typeof animatedStats];
      const increment = (target - start) / steps;

      let current = start;
      let step = 0;

      const timer = setInterval(() => {
        step++;
        current += increment;
        if (step >= steps) {
          current = target;
          clearInterval(timer);
        }
        setAnimatedStats((prev) => ({
          ...prev,
          [key]: Math.floor(current),
        }));
      }, stepDuration);
    });
  };

  const loadStats = async () => {
    try {
      const indicesResponse = await agriculturalAPI.getIndices();
      let totalDocs = 0;
      let totalIndices = 0;
      
      if (indicesResponse.data.success && indicesResponse.data.data) {
        const indices = indicesResponse.data.data;
        totalDocs = indices.reduce((sum: number, idx: any) => sum + (idx.documentCount || 0), 0);
        totalIndices = indices.length;
      }

      let totalCharts = 0;
      try {
        const chartsResponse = await chartsAPI.getCharts(true);
        if (chartsResponse.data.success && chartsResponse.data.data) {
          totalCharts = chartsResponse.data.data.length || 0;
        }
      } catch (chartsError) {
        console.warn('Could not load charts count:', chartsError);
      }

      setStats({
        totalDocuments: totalDocs || 6549887,
        totalIndices: totalIndices || 11,
        totalCharts: totalCharts || 0,
        totalProjects: 150,
        totalUsers: 500,
        totalBusinesses: 250,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({
        totalDocuments: 6549887,
        totalIndices: 11,
        totalCharts: 0,
        totalProjects: 150,
        totalUsers: 500,
        totalBusinesses: 250,
      });
    }
  };

  const platformFeatures = [
    {
      icon: BarChart3,
      title: 'Data Analytics',
      description: 'Transform raw data into actionable insights with our comprehensive analytics suite.',
      gradient: 'from-blue-500 to-cyan-500',
      route: '/analytics',
    },
    {
      icon: Briefcase,
      title: 'Business Directory',
      description: 'Connect with businesses, discover opportunities, and grow your network.',
      gradient: 'from-purple-500 to-pink-500',
      route: '/businesses',
    },
    {
      icon: Newspaper,
      title: 'News & Events',
      description: 'Stay updated with the latest news, events, and industry insights.',
      gradient: 'from-orange-500 to-red-500',
      route: '/news',
    },
    {
      icon: FolderKanban,
      title: 'Project Collaboration',
      description: 'Manage projects, collaborate with teams, and track progress in real-time.',
      gradient: 'from-green-500 to-emerald-500',
      route: '/projects',
    },
    {
      icon: Video,
      title: 'Online Meetings',
      description: 'Conduct virtual meetings, video calls, and collaborative sessions.',
      gradient: 'from-indigo-500 to-purple-500',
      route: '/video-call',
    },
    {
      icon: MessageCircle,
      title: 'Community Forum',
      description: 'Join discussions, share knowledge, and connect with the community.',
      gradient: 'from-cyan-500 to-blue-500',
      route: '/forum',
    },
  ];

  const analyticsTools = [
    {
      title: 'Production Data',
      description: 'Analyze global agricultural production with interactive visualizations',
      icon: BarChart3,
      route: '/visualization',
      gradient: 'from-blue-500 to-blue-600',
      features: ['Interactive Maps', 'Time Series Charts', 'Country Comparisons'],
    },
  ];

  const workflowSteps = [
    {
      title: 'Explore',
      description: 'Connect to any data index with advanced search capabilities. Navigate through millions of records with intelligent field detection.',
      icon: Search,
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Filter',
      description: 'Apply multiple filters simultaneously on any field. Our intelligent filtering system automatically detects field types.',
      icon: Filter,
      gradient: 'from-emerald-500 to-teal-500',
    },
    {
      title: 'Visualize',
      description: 'Create multiple chart types from your filtered data. Automatically generate separate visualizations for each combination.',
      icon: BarChart3,
      gradient: 'from-teal-500 to-cyan-500',
    },
    {
      title: 'Analyze',
      description: 'Perform in-depth data analysis with automatic pattern detection and statistical insights.',
      icon: Brain,
      gradient: 'from-green-600 to-emerald-600',
    },
    {
      title: 'Export',
      description: 'Export your filtered results and visualizations in multiple formats. Share insights with your team.',
      icon: Download,
      gradient: 'from-emerald-600 to-teal-600',
    },
  ];

  const userTypes = [
    {
      title: 'For Researchers',
      description: 'Accelerate your research with powerful filtering and visualization tools. Quickly explore large datasets, identify patterns, and generate publication-ready charts.',
      icon: GraduationCap,
      gradient: 'from-green-500 to-emerald-500',
      features: ['Rapid data exploration', 'Pattern identification', 'Comparative analysis', 'Publication-ready exports'],
    },
    {
      title: 'For Data Analysts',
      description: 'Streamline your analytical workflow with intelligent field detection and automatic chart generation. Focus on insights, not configuration.',
      icon: TrendingUp,
      gradient: 'from-emerald-500 to-teal-500',
      features: ['Automated insights', 'Multi-dimensional analysis', 'Real-time filtering', 'Collaborative sharing'],
    },
    {
      title: 'For Decision Makers',
      description: 'Make data-driven decisions with confidence. Access comprehensive visualizations and insights that help you understand agricultural trends.',
      icon: Target,
      gradient: 'from-teal-500 to-green-600',
      features: ['Executive dashboards', 'Trend analysis', 'Impact assessment', 'Strategic planning support'],
    },
  ];

  const collaborationFeatures = [
    {
      icon: FolderKanban,
      title: 'Project Management',
      description: 'Organize tasks, track progress, and manage your agricultural projects with Kanban boards, Gantt charts, and calendars.',
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      icon: Users2,
      title: 'Team Collaboration',
      description: 'Work together seamlessly with real-time collaboration, team chat, and shared workspaces.',
      gradient: 'from-emerald-500 to-teal-500',
    },
    {
      icon: Video,
      title: 'Online Meetings',
      description: 'Organize video calls, share your screen, and hold virtual meetings with integrated scheduling.',
      gradient: 'from-teal-500 to-cyan-500',
    },
    {
      icon: FileCheck,
      title: 'File Collaboration',
      description: 'Share, edit, and collaborate on documents in real-time with integrated version control.',
      gradient: 'from-green-600 to-emerald-600',
    },
    {
      icon: MessageSquare,
      title: 'Messaging',
      description: 'Communicate with your team via instant messaging, channels, and notifications.',
      gradient: 'from-emerald-600 to-teal-600',
    },
    {
      icon: Calendar,
      title: 'Event Management',
      description: 'Schedule meetings, events, and deadlines with an integrated calendar and automatic reminders.',
      gradient: 'from-teal-600 to-green-600',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden font-sans">
      {/* Particle Canvas Background */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0 opacity-20"
        style={{ background: 'transparent' }}
      />

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://live.staticflickr.com/4123/35609284712_d4a4885a06_6k.jpg')`,
          }}
        >
          {/* Reduced overlay for less shadow */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/5"></div>
        </div>

        {/* Animated Background Elements - Softer */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
        </div>

        {/* Content - Centered */}
        <div className="relative z-10 w-full py-32 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center text-center min-h-[70vh] max-w-5xl mx-auto">
            {/* Centered Text Content */}
            <div className={`space-y-8 ${mounted ? 'animate-fade-in-up' : 'opacity-0'}`}>
              {/* Badge - Modern design */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500/10 via-green-500/10 to-teal-500/10 backdrop-blur-md border border-emerald-400/30 rounded-full text-emerald-700 text-sm font-semibold shadow-lg">
                <Zap className="w-4 h-4 text-emerald-600" />
                <span className="tracking-wide">Complete Analysis & Collaboration Platform</span>
              </div>

              {/* Main Title - Collaboration power and analytical data */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight hero-title-playfair">
                Agriculture meets{' '}
                <span className="block mt-[28px] hero-title-accent">
                  collaboration power and analytical data
                </span>
              </h1>

              {/* Description */}
              <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]">
                Empower your projects with real-time teamwork and data-driven insights for smarter decisions.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 justify-center items-center">
                <button
                  onClick={() => navigate('/search')}
                  className="group relative px-6 py-3 bg-gradient-to-r from-emerald-500 via-emerald-600 to-green-600 text-white rounded-xl font-semibold text-sm shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2 overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  <span className="relative z-10 flex items-center gap-2">
                    <Rocket className="w-4 h-4" />
                    Discover the Platform
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                <button
                  onClick={() => navigate('/businesses')}
                  className="group px-6 py-3 bg-white/90 backdrop-blur-md border-2 border-emerald-400/50 text-emerald-700 rounded-xl font-semibold text-sm hover:bg-emerald-50/90 hover:border-emerald-500 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  <Users className="w-4 h-4" />
                  <span>Explore Directory</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <ChevronRight className="w-6 h-6 text-emerald-600/60 rotate-90" />
        </div>
      </section>

      {/* Stats Strip - NioLand brand section style: "More than X teams..." */}
      <section className="relative py-12 md:py-16 bg-slate-900 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-400 font-normal text-lg mb-10">More than {animatedStats.totalUsers}+ teams are using the platform</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: animatedStats.totalDocuments >= 1000000 ? `${(animatedStats.totalDocuments / 1000000).toFixed(1)}M+` : `${animatedStats.totalDocuments.toLocaleString()}+`, label: 'Documents', sub: 'Indexed data ready for analysis', accent: 'text-sky-400' },
              { value: `${animatedStats.totalProjects}+`, label: 'Projects Completed', sub: 'Track record of delivered projects', accent: 'text-emerald-400' },
              { value: `${animatedStats.totalUsers}+`, label: 'Active Users', sub: 'Teams using the platform', accent: 'text-amber-400' },
              { value: `${animatedStats.totalBusinesses}+`, label: 'Businesses', sub: 'In the directory network', accent: 'text-pink-400' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-1">{stat.value}</div>
                <div className={`text-sm font-semibold ${stat.accent} uppercase tracking-wider mb-1`}>{stat.label}</div>
                <p className="text-sm text-slate-400 max-w-[180px] mx-auto">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Block - NioLand timeline style (nk-timeline) */}
      <section className="py-16 md:py-24 relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(16,185,129,0.06),transparent)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center mb-5">
                <Database className="w-7 h-7" />
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">Collaborate and Ship Insights Faster.</h2>
              <p className="text-xl text-slate-600 leading-relaxed mb-6">Work on data together in real-time, and review and get feedback without the back and forth.</p>
              <ul className="space-y-4 list-none pl-0">
                {[
                  'Streamline collaboration for faster design iteration and efficient shipping, driving productivity and project success.',
                  'Enhance teamwork and accelerate delivery by leveraging collaborative tools that enable seamless communication and efficient workflow.',
                  'Empower your team to work together effectively, ensuring quick turnaround and timely project completion.',
                ].map((text, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-500 mt-2.5" aria-hidden />
                    <p className="text-slate-600 leading-relaxed pt-0.5">{text}</p>
                  </li>
                ))}
              </ul>
              <ul className="flex flex-wrap gap-3 pt-5 list-none pl-0">
                <li>
                  <button onClick={() => navigate('/register')} className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-full font-semibold text-sm hover:bg-emerald-700 transition-all">
                    Create Account
                  </button>
                </li>
                <li>
                  <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="inline-flex items-center gap-2 px-6 py-3 border-2 border-emerald-600 text-emerald-600 rounded-full font-semibold text-sm hover:bg-emerald-50 transition-all">
                    Learn More
                  </button>
                </li>
              </ul>
            </div>
            <div className="relative pl-0 lg:pl-4">
              <div className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-6 shadow-soft">
                <h3 className="text-xl font-bold text-slate-900 mb-4 tracking-tight">Achievement via our distinct methodology</h3>
                <ul className="space-y-4 list-none pl-0">
                  {[
                    { symbol: 'sky', text: 'Manage data with empowerment—explore, filter, and visualize agricultural data with confidence using strategic insights and smart tools.' },
                    { symbol: 'emerald', text: 'Optimize the timely completion of tasks with Kanban, Gantt, and calendars for on-time delivery and efficiency.' },
                    { symbol: 'amber', text: 'Deliver insights in real-time with live analytics and dashboards for informed, data-driven decisions.' },
                  ].map((item, i) => (
                    <li key={i} className="flex gap-4">
                      <span className={`flex-shrink-0 w-3 h-3 rounded-full mt-1.5 ${item.symbol === 'sky' ? 'bg-sky-500' : item.symbol === 'emerald' ? 'bg-emerald-500' : 'bg-amber-500'}`} aria-hidden />
                      <p className="text-slate-600 leading-relaxed text-base">{item.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section - "The Solutions We Offer" NioLand style */}
      <section id="features" className="py-20 md:py-32 relative overflow-hidden bg-gradient-to-b from-slate-50 via-emerald-50/30 to-slate-50">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(16,185,129,0.08),transparent)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Header - NioLand: Features That Get You Results, fs-20, nk-btn-group */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
              <span className="text-emerald-600">Features</span> That Get You Results
            </h2>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Unlock features that deliver tangible results, driving success, growth, and achieving your desired outcomes.
            </p>
            <ul className="flex flex-wrap justify-center gap-3 list-none pl-0">
              <li>
                <button onClick={() => document.getElementById('features-grid')?.scrollIntoView({ behavior: 'smooth' })} className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-full text-sm hover:bg-emerald-700 transition-all">
                  Explore All
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/analytics')} className="px-6 py-3 bg-transparent border-2 border-emerald-600 text-emerald-600 font-semibold rounded-full text-sm hover:bg-emerald-50 transition-all">
                  Learn More
                </button>
              </li>
            </ul>
          </div>

          {/* Feature grid: couleurs intuitives — bleu ciel (data), rose (collaboration), jaune (actualités) */}
          <div id="features-grid" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platformFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              const pastelBg = [
                'bg-sky-100 text-sky-700',       // Data Analytics → bleu ciel
                'bg-emerald-100 text-emerald-700', // Business Directory → vert
                'bg-amber-100 text-amber-700',  // News & Events → jaune
                'bg-emerald-100 text-emerald-700', // Project Collaboration → vert
                'bg-pink-100 text-pink-700',    // Online Meetings → rose
                'bg-sky-100 text-sky-700',      // Community Forum → bleu ciel
              ][index % 6];
              return (
                <div
                  key={index}
                  onClick={() => feature.route && navigate(feature.route)}
                  className="group bg-white/90 backdrop-blur rounded-2xl p-6 border border-slate-200/80 hover:border-emerald-300 hover:shadow-soft transition-all duration-300 cursor-pointer"
                >
                  <div className={`w-14 h-14 rounded-xl ${pastelBg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-sm`}>
                    <IconComponent className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 tracking-tight">{feature.title}</h3>
                  <p className="text-slate-600 mb-4 leading-relaxed">{feature.description}</p>
                  <button className="group/btn flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium text-sm">
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Subsection: Project Features — couleurs pour repérage (rose = collaboration, bleu = outils) */}
          <div className="mt-24 pt-20 border-t border-slate-200/80">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 tracking-tight">
                <span className="text-pink-600">Project</span> Features That Get You Results
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Everything you need to manage projects, collaborate with teams, and deliver on time. From tasks to meetings to files—all in one place.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => navigate('/projects')}
                  className="px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Explore All
                </button>
              <button
                onClick={() => document.getElementById('backoffice')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-6 py-3 bg-white text-pink-600 font-medium rounded-lg border-2 border-pink-300 hover:bg-pink-50 transition-colors"
              >
                Learn More
              </button>
            </div>
          </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: FolderKanban, label: 'Task Management', bg: 'bg-emerald-100', iconColor: 'text-emerald-700' },
                { icon: GanttChart, label: 'Gantt & Timeline', bg: 'bg-sky-100', iconColor: 'text-sky-700' },
                { icon: CalendarIcon, label: 'Calendar & Meetings', bg: 'bg-amber-100', iconColor: 'text-amber-700' },
                { icon: Users2, label: 'Team Collaboration', bg: 'bg-pink-100', iconColor: 'text-pink-700' },
                { icon: Video, label: 'Video Meetings', bg: 'bg-pink-100', iconColor: 'text-pink-700' },
                { icon: FileCheck, label: 'File Collaboration', bg: 'bg-sky-100', iconColor: 'text-sky-700' },
                { icon: MessageSquare, label: 'Messaging', bg: 'bg-amber-100', iconColor: 'text-amber-700' },
                { icon: Target, label: 'Planning Poker', bg: 'bg-emerald-100', iconColor: 'text-emerald-700' },
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={index}
                    onClick={() => navigate('/projects')}
                    className="group flex flex-col items-center text-center p-6 rounded-2xl hover:bg-white/80 transition-all duration-300 cursor-pointer"
                  >
                    <div className={`w-16 h-16 rounded-xl ${item.bg} flex items-center justify-center mb-4 group-hover:scale-105 transition-transform ${item.iconColor}`}>
                      <IconComponent className="w-8 h-8" />
                    </div>
                    <span className="font-semibold text-slate-900 text-sm tracking-tight">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Analytics Section */}
      <section id="analytics" className="py-20 md:py-32 relative overflow-hidden bg-white">
        {/* Rich background: mesh + grid */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-[70%] bg-gradient-to-b from-sky-50/40 via-emerald-50/30 to-transparent" />
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-sky-200/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-emerald-200/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 right-1/3 w-[300px] h-[300px] bg-amber-100/20 rounded-full blur-3xl" />
          <div 
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgb(15 23 42 / 0.15) 1px, transparent 0)`,
              backgroundSize: '32px 32px',
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div
            className={`text-center mb-16 md:mb-20 ${
              visibleSections.has('analytics') ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            <p className="text-xs font-bold tracking-widest text-sky-600 uppercase mb-2">
              Scalable Solutions
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3 leading-tight tracking-tight">
              Accelerate Your
              <span className="block text-sky-700 mt-1">Agricultural Platform Development</span>
            </h2>
            <p className="text-sm md:text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Showcase the key features of your Living Lab: analytics, data exploration,
              business directory, and project collaboration. Add more screens as needed.
            </p>
          </div>
        </div>

        {/* Mini Sections Grid - Full Width */}
        <div className="space-y-0">
          {/* Data Analytics Section */}
          <div className={`py-16 md:py-24 bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50 ${
              visibleSections.has('analytics') ? 'animate-fade-in-up animation-delay-100' : 'opacity-0'
            }`}>
              <div className="w-full">
                <div className="grid md:grid-cols-2 gap-16 lg:gap-24 xl:gap-32 items-center">
                  {/* Left: Content */}
                  <div className="space-y-6 pl-4 sm:pl-6 md:pl-8 lg:pl-12 xl:pl-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full text-emerald-600 text-sm font-semibold shadow-sm">
                      <BarChart3 className="w-4 h-4" />
                      <span>Data Analytics</span>
                    </div>
                    <h3 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight tracking-tight">Advanced Analytics Suite</h3>
                    <p className="text-lg text-slate-700 leading-relaxed">
                      Transform raw data into actionable insights with our comprehensive analytics suite. Explore, visualize, and understand your agricultural data like never before.
                    </p>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      {[
                        { icon: BarChart3, text: 'Dynamic chart creation' },
                        { icon: Filter, text: 'Multi-filter analysis' },
                        { icon: Brain, text: 'Intelligent data exploration' },
                        { icon: Activity, text: 'Real-time visualizations' },
                      ].map((feature, idx) => {
                        const IconComponent = feature.icon;
                        return (
                          <div key={idx} className="flex items-center gap-3">
                            <IconComponent className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-800">{feature.text}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="pt-4">
                      <button 
                        onClick={() => navigate('/analytics')}
                        className="group/btn inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl"
                      >
                        <span>Explore Analytics</span>
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Right: Dashboard Visual with statproduction image */}
                  <div className="relative pr-4 sm:pr-6 lg:pr-8 xl:pr-12">
                    <div className="relative">
                      {/* Image Container - Fixed size, no hover effects */}
                      <div className="rounded-lg shadow-2xl relative overflow-hidden border-4 border-white">
                        <img 
                          src="/statproduction.PNG" 
                          alt="Statistics Production Dashboard" 
                          className="w-[600px] h-auto object-contain"
                          style={{ maxWidth: 'none' }}
                        />
                      </div>
                      {/* Decorative Elements */}
                      <div className="absolute -top-4 -left-4 w-16 h-16 bg-green-200 rounded-full opacity-30 blur-xl"></div>
                      <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-emerald-200 rounded-full opacity-30 blur-xl"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Directory Section */}
            <div className={`py-16 md:py-24 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 ${
              visibleSections.has('analytics') ? 'animate-fade-in-up animation-delay-200' : 'opacity-0'
            }`}>
              <div className="w-full">
                <div className="grid md:grid-cols-2 gap-16 lg:gap-24 xl:gap-32 items-center">
                  {/* Left: Content */}
                  <div className="space-y-6 pl-4 sm:pl-6 md:pl-8 lg:pl-12 xl:pl-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full text-emerald-600 text-sm font-semibold shadow-sm">
                      <Briefcase className="w-4 h-4" />
                      <span>Business Directory</span>
                    </div>
                    <h3 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight tracking-tight">Connect & Network</h3>
                    <p className="text-lg text-slate-700 leading-relaxed">
                      Connect with businesses, discover opportunities, and grow your network. Find partners, suppliers, and collaborators in the agricultural ecosystem.
                    </p>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      {[
                        { icon: Building2, text: 'Business profiles' },
                        { icon: Search, text: 'Advanced search' },
                        { icon: Network, text: 'Network building' },
                        { icon: Lightbulb, text: 'Opportunity discovery' },
                      ].map((feature, idx) => {
                        const IconComponent = feature.icon;
                        return (
                          <div key={idx} className="flex items-center gap-3">
                            <IconComponent className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-800">{feature.text}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="pt-4">
                      <button 
                        onClick={() => navigate('/businesses')}
                        className="group/btn inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl"
                      >
                        <span>Learn More</span>
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Right: Dashboard Visual with businessdirectory image */}
                  <div className="relative pr-4 sm:pr-6 lg:pr-8 xl:pr-12">
                    <div className="relative">
                      {/* Image Container - Fixed size, no hover effects */}
                      <div className="rounded-lg shadow-2xl relative overflow-hidden border-4 border-white">
                        <img 
                          src="/buisnessdirectoey.PNG" 
                          alt="Business Directory" 
                          className="w-[600px] h-auto object-contain"
                          style={{ maxWidth: 'none' }}
                        />
                      </div>
                      {/* Decorative Elements */}
                      <div className="absolute -top-4 -right-4 w-16 h-16 bg-emerald-200 rounded-full opacity-30 blur-xl"></div>
                      <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-teal-200 rounded-full opacity-30 blur-xl"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Collaboration Section */}
            <div className={`py-16 md:py-24 bg-gradient-to-r from-teal-50 via-emerald-50 to-green-50 ${
              visibleSections.has('analytics') ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'
            }`}>
              <div className="w-full">
                <div className="grid md:grid-cols-2 gap-16 lg:gap-24 xl:gap-32 items-center">
                  {/* Left: Content */}
                  <div className="space-y-6 pl-4 sm:pl-6 md:pl-8 lg:pl-12 xl:pl-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full text-emerald-600 text-sm font-semibold shadow-sm">
                      <FolderKanban className="w-4 h-4" />
                      <span>Project Collaboration</span>
                    </div>
                    <h3 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight tracking-tight">Team Collaboration Tools</h3>
                    <p className="text-lg text-slate-700 leading-relaxed">
                      Manage projects, collaborate with teams, and track progress in real-time. Organize tasks with Kanban boards, Gantt charts, and timelines.
                    </p>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      {[
                        { icon: FolderKanban, text: 'Kanban boards' },
                        { icon: GanttChart, text: 'Gantt charts' },
                        { icon: Users2, text: 'Real-time collaboration' },
                        { icon: TrendingUp, text: 'Progress tracking' },
                      ].map((feature, idx) => {
                        const IconComponent = feature.icon;
                        return (
                          <div key={idx} className="flex items-center gap-3">
                            <IconComponent className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-800">{feature.text}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="pt-4">
                      <button 
                        onClick={() => navigate('/projects')}
                        className="group/btn inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl"
                      >
                        <span>Learn More</span>
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Right: Dashboard Visual with collaboration image */}
                  <div className="relative pr-4 sm:pr-6 lg:pr-8 xl:pr-12">
                    <div className="relative">
                      {/* Image Container - Fixed size, no hover effects */}
                      <div className="rounded-lg shadow-2xl relative overflow-hidden border-4 border-white">
                        <img 
                          src="/collaboration.PNG" 
                          alt="Team Collaboration Tools" 
                          className="w-[600px] h-auto object-contain"
                          style={{ maxWidth: 'none' }}
                        />
                      </div>
                      {/* Decorative Elements */}
                      <div className="absolute -top-4 -left-4 w-16 h-16 bg-emerald-200 rounded-full opacity-30 blur-xl"></div>
                      <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-green-200 rounded-full opacity-30 blur-xl"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* Workflow Section - Capture-style design */}
      <section id="workflow" className="py-20 md:py-32 relative overflow-hidden bg-gradient-to-b from-slate-50 to-emerald-50/40">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_80%_at_80%_50%,rgba(16,185,129,0.06),transparent)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Header: OUR PROCESS style */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-emerald-600 font-bold text-sm uppercase tracking-widest mb-4">How It Works</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
              From Data to Insights in <span className="text-emerald-600">Five Steps</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our intelligent workflow helps you explore, filter, visualize, and understand your agricultural data.
            </p>
          </div>

          {/* Process flow: couleurs par étape pour repérage visuel rapide */}
          <div className="relative mb-12">
            <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-200 via-sky-200 to-emerald-200 -translate-y-1/2 z-0" />
            <div className="flex flex-wrap justify-center gap-6 md:gap-4 md:justify-between max-w-4xl mx-auto">
              {[
                { num: 1, bg: 'bg-emerald-100', border: 'border-emerald-200', text: 'text-emerald-700' },
                { num: 2, bg: 'bg-sky-100', border: 'border-sky-200', text: 'text-sky-700' },
                { num: 3, bg: 'bg-amber-100', border: 'border-amber-200', text: 'text-amber-700' },
                { num: 4, bg: 'bg-pink-100', border: 'border-pink-200', text: 'text-pink-700' },
                { num: 5, bg: 'bg-emerald-100', border: 'border-emerald-200', text: 'text-emerald-700' },
              ].map((step) => (
                <div key={step.num} className="relative z-10 flex flex-col items-center">
                  <div className={`w-16 h-16 rounded-full ${step.bg} flex items-center justify-center border-2 ${step.border}`}>
                    <span className={`text-xl font-bold ${step.text}`}>{step.num}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Step descriptions: STEP X + title + description, left-aligned columns */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">
            {[
              { number: 1, title: "Explore", description: "Connect to any data index with advanced search capabilities." },
              { number: 2, title: "Filter", description: "Apply multiple filters simultaneously on any field." },
              { number: 3, title: "Visualize", description: "Create multiple chart types from your filtered data." },
              { number: 4, title: "Analyze", description: "Perform in-depth data analysis with automatic pattern detection." },
              { number: 5, title: "Export", description: "Export your results and visualizations in multiple formats." },
            ].map((step) => (
              <div key={step.number} className="text-left">
                <span className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Step {step.number}</span>
                <h3 className="text-lg font-bold text-slate-900 mb-3 tracking-tight">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dark Impact Section - NioLand Green Tech "Join the revolution" style - innovative single-palette */}
      <section className="py-20 md:py-28 relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_100%,rgba(16,185,129,0.12),transparent_50%)] pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6 tracking-tight leading-tight">
            Join the data revolution
          </h2>
          <p className="text-lg text-slate-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            More than {animatedStats.totalUsers}+ teams use the platform. Together we can turn agricultural data into impact—sustainable, measurable, and scalable.
          </p>
          <button
            onClick={() => navigate('/search')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-400 transition-all shadow-lg hover:shadow-emerald-500/25"
          >
            Get Started
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Roles Section */}
      <section className="py-20 md:py-32 relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-50/30 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(ellipse_80%_50%_at_100%_0%,rgba(16,185,129,0.05),transparent)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-emerald-600 font-bold text-sm uppercase tracking-widest mb-4">Empowering Every Role</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">Whether You're a Researcher, Analyst, or Decision Maker</h2>
            <p className="text-lg text-slate-600 leading-relaxed">Our platform adapts to your needs in the agricultural sector.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {[
              { id: "researchers", icon: FlaskConical, label: "For Researchers", color: "emerald" },
              { id: "analysts", icon: LineChart, label: "For Data Analysts", color: "sky" },
              { id: "decision-makers", icon: Briefcase, label: "For Decision Makers", color: "amber" },
            ].map((role) => {
              const IconComponent = role.icon;
              const isActive = activeRole === role.id;
              const colorClasses = {
                emerald: isActive ? "bg-emerald-600 text-white" : "bg-emerald-50 border-emerald-200 text-emerald-600",
                sky: isActive ? "bg-sky-500 text-white" : "bg-sky-50 border-sky-200 text-sky-600",
                amber: isActive ? "bg-amber-500 text-white" : "bg-amber-50 border-amber-200 text-amber-600",
              };
              return (
                <button
                  key={role.id}
                  onClick={() => setActiveRole(role.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full border-2 font-medium transition-all duration-300 ${colorClasses[role.color as keyof typeof colorClasses]}`}
                >
                  <IconComponent className="w-5 h-5" />
                  {role.label}
                </button>
              );
            })}
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl border border-slate-200/80 p-8 md:p-12 shadow-soft">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  {(() => {
                    const roles = {
                      researchers: { icon: FlaskConical, label: "For Researchers", title: "Intelligent Platform", description: "Accelerate your research with powerful filtering and visualization tools.", features: ["Rapid data exploration", "Pattern identification"], color: "emerald" },
                      analysts: { icon: LineChart, label: "For Data Analysts", title: "Advanced Analytics", description: "Dive deep into agricultural data with advanced statistical analysis tools.", features: ["Statistical analysis", "Automated reports"], color: "sky" },
                      "decision-makers": { icon: Briefcase, label: "For Decision Makers", title: "Strategic Insights", description: "Make informed decisions based on concrete data.", features: ["Executive dashboards", "Real-time KPIs"], color: "amber" },
                    };
                    const currentRole = roles[activeRole as keyof typeof roles] || roles.researchers;
                    const RoleIcon = currentRole.icon;
                    const roleColorClasses = {
                      emerald: "bg-emerald-50 border-emerald-200 text-emerald-600",
                      sky: "bg-sky-50 border-sky-200 text-sky-600",
                      amber: "bg-amber-50 border-amber-200 text-amber-600",
                    };
                    return (
                      <>
                        <div className={`inline-flex items-center gap-2 ${roleColorClasses[currentRole.color as keyof typeof roleColorClasses]} rounded-full px-4 py-2 mb-6 border`}>
                          <RoleIcon className="w-4 h-4" />
                          <span className="font-medium text-sm">{currentRole.label}</span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-4 tracking-tight">{currentRole.title}</h3>
                        <p className="text-slate-600 mb-6 leading-relaxed">{currentRole.description}</p>
                        <div className="space-y-3 mb-8">
                          {currentRole.features.map((feature) => (
                            <div key={feature} className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${roleColorClasses[currentRole.color as keyof typeof roleColorClasses]} border`}>
                                <Sparkles className="w-4 h-4" />
                              </div>
                              <span className="text-slate-900 font-medium">{feature}</span>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => navigate('/search')}
                          className="group px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-all flex items-center gap-2"
                        >
                          View Presentation
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                      </>
                    );
                  })()}
                </div>
                <div className="relative">
                  {(() => {
                    const roleColors: Record<string, string> = {
                      researchers: 'bg-emerald-600',
                      analysts: 'bg-sky-500',
                      'decision-makers': 'bg-amber-500',
                    };
                    return (
                      <>
                        <div className={`absolute -inset-4 rounded-3xl blur-2xl opacity-30 ${roleColors[activeRole as keyof typeof roleColors] || 'bg-emerald-600'}`} />
                        <div className="relative bg-slate-50 rounded-2xl p-4 border border-slate-200 overflow-hidden">
                          <img
                            src="/search.PNG"
                            alt="Search"
                            className="w-full h-auto object-contain rounded-lg"
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


    


      {/* Backoffice Section */}
      <section id="backoffice" className="py-20 md:py-32 relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-slate-50">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_20%_80%,rgba(16,185,129,0.06),transparent)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-emerald-600 font-bold text-sm uppercase tracking-widest mb-4">Collaboration</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
              Powerful <span className="text-emerald-600">Backoffice Features</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Manage your agricultural projects, collaborate with your teams, organize meetings, and work together seamlessly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: FolderKanban, label: "Project Management", description: "Organize tasks, track progress, and manage your agricultural projects with Kanban boards, Gantt charts, and calendars." },
              { icon: Users2, label: "Team Collaboration", description: "Work together seamlessly with real-time collaboration, team chat, and shared workspaces." },
              { icon: Video, label: "Online Meetings", description: "Organize video calls, share your screen, and hold virtual meetings with integrated scheduling." },
              { icon: FileCheck, label: "File Collaboration", description: "Share, edit, and collaborate on documents in real-time with integrated version control." },
            ].map((tab) => {
              const IconComponent = tab.icon;
              return (
                <div key={tab.label} className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-slate-200/80 hover:border-emerald-300 hover:shadow-soft transition-all group">
                  <div className="w-14 h-14 rounded-xl bg-emerald-50 flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-colors shadow-sm">
                    <IconComponent className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight">{tab.label}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{tab.description}</p>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-3xl border border-gray-200 p-8 md:p-12 mb-16">
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { id: "projects", icon: FolderKanban, label: "Project Management" },
                { id: "team", icon: Users2, label: "Team Collaboration" },
                { id: "meetings", icon: Video, label: "Online Meetings" },
                { id: "files", icon: FileCheck, label: "File Collaboration" },
                { id: "messaging", icon: MessageSquare, label: "Messaging" },
                { id: "events", icon: Calendar, label: "Event Management" },
              ].map((tab) => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveBackofficeTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium text-sm transition-all ${
                      activeBackofficeTab === tab.id ? "bg-emerald-600 text-white shadow-md" : "bg-gray-50 text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-extrabold text-slate-900 mb-4 tracking-tight">
                  {[
                    { id: "projects", title: "Project Management", description: "Organize tasks, track progress, and manage your agricultural projects with Kanban boards, Gantt charts, and calendars." },
                    { id: "team", title: "Team Collaboration", description: "Work together seamlessly with real-time collaboration, team chat, and shared workspaces." },
                    { id: "meetings", title: "Online Meetings", description: "Organize video calls, share your screen, and hold virtual meetings with integrated scheduling." },
                    { id: "files", title: "File Collaboration", description: "Share, edit, and collaborate on documents in real-time with integrated version control." },
                    { id: "messaging", title: "Messaging", description: "Communicate instantly with your team through our integrated and secure messaging system." },
                    { id: "events", title: "Event Management", description: "Plan, organize, and manage your agricultural events with comprehensive management tools." },
                  ].find(t => t.id === activeBackofficeTab)?.title || "Project Management"}
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {[
                    { id: "projects", title: "Project Management", description: "Organize tasks, track progress, and manage your agricultural projects with Kanban boards, Gantt charts, and calendars." },
                    { id: "team", title: "Team Collaboration", description: "Work together seamlessly with real-time collaboration, team chat, and shared workspaces." },
                    { id: "meetings", title: "Online Meetings", description: "Organize video calls, share your screen, and hold virtual meetings with integrated scheduling." },
                    { id: "files", title: "File Collaboration", description: "Share, edit, and collaborate on documents in real-time with integrated version control." },
                    { id: "messaging", title: "Messaging", description: "Communicate instantly with your team through our integrated and secure messaging system." },
                    { id: "events", title: "Event Management", description: "Plan, organize, and manage your agricultural events with comprehensive management tools." },
                  ].find(t => t.id === activeBackofficeTab)?.description || "Organize tasks, track progress, and manage your agricultural projects with Kanban boards, Gantt charts, and calendars."}
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-md">
                <img
                  src="/collaborations.PNG"
                  alt="Project Management - Kanban, Gantt, calendars"
                  className="w-full h-auto object-cover max-h-[400px] object-center"
                />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { value: "150+", label: "Projects", icon: Briefcase },
              { value: "500+", label: "Users", icon: UserCircle },
              { value: "250+", label: "Businesses", icon: Building2 },
            ].map((stat) => {
              const IconComponent = stat.icon;
              return (
                <div key={stat.label} className="bg-white/90 backdrop-blur rounded-2xl p-8 border border-slate-200/80 text-center group hover:border-emerald-300 hover:shadow-soft transition-all">
                  <div className="w-14 h-14 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-sm">
                    <IconComponent className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div className="text-4xl font-extrabold text-slate-900 mb-2 tracking-tight">{stat.value}</div>
                  <div className="text-slate-600 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section - NioLand: "Not convinced..." + Start Free Trial / Login */}
      <section className="relative overflow-hidden py-16 md:py-24 bg-emerald-600">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(0,0,0,0.08),transparent)] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6 tracking-tight">
            Not convinced you're the one? We love to take a challenge.
          </h2>
          <ul className="flex flex-wrap justify-center gap-4 list-none pl-0">
            <li>
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-700 rounded-full font-semibold text-base hover:bg-slate-50 transition-all shadow-lg"
              >
                Start Your Free Trial
              </button>
            </li>
            <li>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-white/80 text-white rounded-full font-semibold text-base hover:bg-white/10 transition-all"
              >
                Login to Your Account
              </button>
            </li>
          </ul>
        </div>
      </section>

      <style>{`
        /* Playfair Display Typography for Hero Title */
        .hero-title-playfair {
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 700;
          letter-spacing: -0.025em;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        .hero-title-accent {
          background: linear-gradient(135deg, #fef08a 0%, #facc15 50%, #eab308 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          filter: drop-shadow(0 2px 6px rgba(0,0,0,0.6)) drop-shadow(0 4px 12px rgba(0,0,0,0.5));
          -webkit-text-stroke: 1px rgba(0,0,0,0.3);
        }
        
        /* Responsive font sizes - Playfair Display */
        @media (max-width: 640px) {
          .hero-title-playfair {
            font-size: 2.25rem; /* 36px */
          }
        }
        
        @media (min-width: 640px) {
          .hero-title-playfair {
            font-size: 3rem; /* 48px */
          }
        }
        
        @media (min-width: 768px) {
          .hero-title-playfair {
            font-size: 3.75rem; /* 60px */
          }
        }
        
        @media (min-width: 1024px) {
          .hero-title-playfair {
            font-size: 4.5rem; /* 72px */
          }
        }
        
        #hero .inline-flex {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          font-weight: 500;
          letter-spacing: 0.02em;
        }
        
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          33% {
            transform: translate(10px, -15px) rotate(2deg);
          }
          66% {
            transform: translate(-10px, 15px) rotate(-2deg);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.05);
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .animate-fade-in-left {
          animation: fadeInLeft 0.6s ease-out forwards;
        }

        .animate-fade-in-right {
          animation: fadeInRight 0.6s ease-out forwards;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }

        .animation-delay-50 {
          animation-delay: 50ms;
        }

        .animation-delay-100 {
          animation-delay: 100ms;
        }

        .animation-delay-150 {
          animation-delay: 150ms;
        }

        .animation-delay-200 {
          animation-delay: 200ms;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          33% {
            transform: translate(10px, -15px) rotate(2deg);
          }
          66% {
            transform: translate(-10px, 15px) rotate(-2deg);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.05);
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }

        /* Modern card hover effects */
        @media (hover: hover) {
          .group:hover {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default EleavingLabHome;
